import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, DataCard } from "@/components/admin/AdminPrimitives";
import { Skeleton } from "@/components/ui/skeleton";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";

export const Route = createFileRoute("/_authenticated/admin/receita")({
  head: () => ({ meta: [{ title: "Receita — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ReceitaAdmin,
});

function fmtBRL(cents: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
}

function ReceitaAdmin() {
  const q = useQuery({
    queryKey: ["admin.receita"],
    queryFn: async () => {
      const start = new Date(Date.now() - 90 * 86400000).toISOString();
      const { data } = await supabase.from("orders").select("total_cents, paid_at").eq("status", "paid").gte("paid_at", start);
      const buckets: Record<string, number> = {};
      for (let i = 89; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400000);
        buckets[d.toISOString().slice(0, 10)] = 0;
      }
      let total = 0;
      for (const r of (data ?? []) as any[]) {
        const k = String(r.paid_at).slice(0, 10);
        if (k in buckets) buckets[k] += r.total_cents ?? 0;
        total += r.total_cents ?? 0;
      }
      return {
        total,
        series: Object.entries(buckets).map(([date, cents]) => ({ date: date.slice(5), revenue: cents / 100 })),
      };
    },
  });
  return (
    <div>
      <PageHeader title="Receita" description="Faturamento consolidado da plataforma." />
      <div className="grid gap-4 lg:grid-cols-3">
        <DataCard>
          <div className="p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Últimos 90 dias</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-[var(--green)]">
              {q.isLoading ? "—" : fmtBRL(q.data?.total ?? 0)}
            </p>
          </div>
        </DataCard>
        <DataCard>
          <div className="p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Ticket médio</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-[var(--navy)]">R$ 47,00</p>
          </div>
        </DataCard>
        <DataCard>
          <div className="p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Modelo</p>
            <p className="mt-2 font-display text-lg font-extrabold text-[var(--navy)]">Pagamento único · Acesso vitalício</p>
          </div>
        </DataCard>
      </div>
      <DataCard>
        <div className="p-5">
          <p className="mb-3 font-display text-sm font-extrabold text-[var(--navy)]">Série histórica (90 dias)</p>
          <div className="h-80">
            {q.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={q.data?.series ?? []}>
                  <defs>
                    <linearGradient id="rev2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--green)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--green)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="rgba(0,0,0,0.4)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="rgba(0,0,0,0.4)" />
                  <RTooltip
                    formatter={(v: any) => [`R$ ${Number(v).toFixed(2)}`, "Receita"]}
                    contentStyle={{ borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="var(--green)" strokeWidth={2} fill="url(#rev2)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </DataCard>
    </div>
  );
}
